import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Descriptions, Tag, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Input } from 'components/input/input';
import { Select } from 'components/select/select';
import { Button, buttonType } from 'components/button/button';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { SellUtopiaDestination, SellUtopiaManualResponse } from 'core/domain';
import {
    BankAccountOption,
    UserOption,
    getUserBankAccounts,
    searchUsers,
    sellUtopiaManual,
} from 'core/repositories/sell-utopia-manual';
import { digitSperator, faNumToEnNum } from 'core/utils/decimalExtentions';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';
import './sell-utopia-manual.scss';

type Option = { label: string; value: string };

interface FormValues {
    user?: UserOption;
    voucherCode: string;
    from: string;
    to: string;
    destination: SellUtopiaDestination;
    bankAccountId?: string;
}

const emptyForm: FormValues = {
    user: undefined,
    voucherCode: '',
    from: '',
    to: '',
    destination: SellUtopiaDestination.Wallet,
    bankAccountId: undefined,
};

const destinationOptions: Option[] = [
    { label: 'کیف پول', value: String(SellUtopiaDestination.Wallet) },
    { label: 'حساب بانکی', value: String(SellUtopiaDestination.BankAccount) },
];

const CopyableValue = ({ value }: { value?: string | number }) => {
    const text = value === undefined || value === null || value === '' ? '' : String(value);

    return (
        <Typography.Text
            className="en"
            ellipsis={{ tooltip: text || '-' }}
            copyable={text ? { text, icon: [<CopyOutlined />, <CopyOutlined />] } : false}
        >
            {text || '-'}
        </Typography.Text>
    );
};

export const SellUtopiaManual: React.FC = () => {
    const [form, setForm] = useState<FormValues>(emptyForm);
    const [result, setResult] = useState<SellUtopiaManualResponse>();

    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [bankAccounts, setBankAccounts] = useState<BankAccountOption[]>([]);
    const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const fromRef = useRef(null);
    const toRef = useRef(null);

    const isBankDestination = form.destination === SellUtopiaDestination.BankAccount;

    // شماره موبایل کاربر انتخاب شده، برای فیلتر کردن حساب های بانکی همان کاربر.
    const selectedUserPhoneNumber = form.user?.phoneNumber ?? '';

    const onUserSearch = (keyword: string) => {
        if (!keyword) return;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => searchUsers(setUsersLoading, setUserOptions, keyword), 1000);
    };

    useEffect(() => {
        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, []);

    useEffect(() => {
        if (!isBankDestination || !selectedUserPhoneNumber) {
            setBankAccounts([]);
            return;
        }
        getUserBankAccounts(setBankAccountsLoading, setBankAccounts, selectedUserPhoneNumber);
    }, [isBankDestination, selectedUserPhoneNumber]);

    const onSubmit = () => {
        if (!form.user) return message.warning('کاربر را انتخاب کنید.');
        if (!form.voucherCode.trim()) return message.warning('کد ووچر را وارد کنید.');
        if (!form.from || !form.to) return message.warning('بازه زمانی را انتخاب کنید.');
        if (isBankDestination && !form.bankAccountId) return message.warning('حساب بانکی مقصد را انتخاب کنید.');

        setResult(undefined);
        sellUtopiaManual(
            setSubmitting,
            setResult,
            {
                userId: form.user.value,
                voucherCode: form.voucherCode.trim(),
                startDate: jFormat(form.from),
                endDate: jFormatEndOfDay(form.to),
                destination: form.destination,
                bankAccountId: isBankDestination ? form.bankAccountId : undefined,
            },
            () => {
                message.success('فروش با موفقیت ثبت شد.');
                setForm(emptyForm);
                setUserOptions([]);
                setBankAccounts([]);
            },
        );
    };

    return (
        <Page className="pmx-sell-utopia-manual" title="فروش دستی یوتوپیا">
            <Card title="فروش دستی یوتوپیا">
                <div className="select">
                    <label>کاربر</label>
                    <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={usersLoading}
                        placeholder="نام یا شماره موبایل کاربر را جستجو کنید"
                        notFoundContent={usersLoading ? 'در حال جستجو...' : 'یافت نشد'}
                        value={form.user?.value}
                        options={userOptions}
                        onSearch={onUserSearch}
                        onChange={(value) =>
                            setForm({
                                ...form,
                                user: userOptions.find((option) => option.value === value),
                                bankAccountId: undefined,
                            })
                        }
                    />
                </div>

                <div className="dates">
                    <DatePicker
                        className="date-picker"
                        value={form.from}
                        calendar={persian}
                        ref={fromRef}
                        locale={persian_fa}
                        render={
                            <div>
                                <Input value={form.from} icon={<CalendarIcon />} placeholder="از تاریخ" />
                            </div>
                        }
                        onChange={(date: any) => setForm({ ...form, from: date.format() })}
                    />
                    <DatePicker
                        className="date-picker"
                        value={form.to}
                        calendar={persian}
                        ref={toRef}
                        locale={persian_fa}
                        render={
                            <div>
                                <Input value={form.to} icon={<CalendarIcon />} placeholder="تا تاریخ" />
                            </div>
                        }
                        onChange={(date: any) => setForm({ ...form, to: date.format() })}
                    />
                </div>

                <Input
                    value={form.voucherCode}
                    label="کد ووچر"
                    placeholder="USD-XXXX-XXXX-XXXX-XXXX-XXXX"
                    dir="ltr"
                    maxLength={80}
                    onChange={(e) => setForm({ ...form, voucherCode: e.target.value })}
                    onPressEnter={onSubmit}
                />

                <div className="select">
                    <label>مقصد تسویه</label>
                    <Select
                        value={String(form.destination)}
                        options={destinationOptions}
                        onChange={(value) =>
                            setForm({
                                ...form,
                                destination: Number(value) as SellUtopiaDestination,
                                bankAccountId: undefined,
                            })
                        }
                    />
                </div>

                {isBankDestination && (
                    <div className="select">
                        <label>حساب بانکی مقصد</label>
                        <Select
                            showSearch
                            allowClear
                            loading={bankAccountsLoading}
                            disabled={!form.user}
                            placeholder={form.user ? 'شبا یا شماره کارت را جستجو کنید' : 'ابتدا کاربر را انتخاب کنید'}
                            notFoundContent={
                                bankAccountsLoading ? 'در حال دریافت...' : 'حساب بانکی تایید شده ای یافت نشد'
                            }
                            value={form.bankAccountId}
                            options={bankAccounts}
                            // ارقام فارسی هم باید جست و جو شوند، پس ورودی نرمال می شود.
                            filterOption={(input, option) =>
                                ((option as BankAccountOption | undefined)?.search ?? '').includes(
                                    faNumToEnNum(input).trim().toLowerCase(),
                                )
                            }
                            onChange={(value) => setForm({ ...form, bankAccountId: value })}
                        />
                    </div>
                )}

                <div className="buttons">
                    <Button
                        type={buttonType.success}
                        text="ثبت فروش"
                        loading={submitting}
                        disabled={submitting}
                        onClick={onSubmit}
                    />
                </div>
            </Card>

            {result && (
                <Card title="نتیجه ثبت" className="sell-result">
                    <section>
                        <Tag
                            color={
                                result.resumedExistingOrderTransaction
                                    ? 'blue'
                                    : result.reusedExistingDeposit
                                      ? 'gold'
                                      : 'green'
                            }
                        >
                            {result.resumedExistingOrderTransaction
                                ? 'سفارش از قبل وجود داشت؛ فقط برداشت جامانده ساخته و به آن وصل شد.'
                                : result.reusedExistingDeposit
                                  ? 'از واریز موجود استفاده شد؛ واریز جدیدی ساخته نشد.'
                                  : 'واریز جدید ثبت شد.'}
                        </Tag>
                        <Descriptions className="result-desc" colon={false} column={1}>
                            <Descriptions.Item label="کد پیگیری سفارش:">
                                <CopyableValue value={result.orderTransactionTrackingCode} />
                            </Descriptions.Item>
                            <Descriptions.Item label="کد پیگیری واریز:">
                                <CopyableValue value={result.depositTransactionTrackingCode} />
                            </Descriptions.Item>
                            <Descriptions.Item label="کد پیگیری معامله:">
                                <CopyableValue value={result.orderTrackingCode} />
                            </Descriptions.Item>
                            <Descriptions.Item label="مقدار ووچر:">
                                <CopyableValue value={digitSperator(result.voucherAmount)} />
                            </Descriptions.Item>
                            <Descriptions.Item label="مبلغ ریالی:">
                                <CopyableValue value={digitSperator(result.destinationAmount)} />
                            </Descriptions.Item>
                            {result.withdrawalTransactionTrackingCode && (
                                <Descriptions.Item label="کد پیگیری برداشت:">
                                    <CopyableValue value={result.withdrawalTransactionTrackingCode} />
                                </Descriptions.Item>
                            )}
                            {result.iBan && (
                                <Descriptions.Item label="شبا مقصد:">
                                    <CopyableValue value={result.iBan} />
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        {result.withdrawalTransactionTrackingCode && (
                            <p className="hint">
                                برداشت ریالی در وضعیت «در انتظار ادمین» ثبت شد؛ تسویه آن از صفحه «برداشت های ریالی در
                                انتظار» انجام می شود.
                            </p>
                        )}
                    </section>
                </Card>
            )}
        </Page>
    );
};
